import type { Order, OrderStatus, PaymentStatus, ShippingMeta } from "~/types";

// Ported from /tmp/fnd-design-pkg/b2b-foundation/project/components/data.jsx.
// Enrichments per plan § Wave 1 Task D:
//  - paymentStatus: 'paid' when status==='delivered' AND date is older than 14 days
//    (relative to 2026-05-29); else 'unpaid'.
//  - shipping defaults to { method:'logist', address:'sklad' } (React source
//    didn't carry structured shipping info — only a freeform courier label).
//  - timeline: single synthesized entry with the order's date + current stage.
//  - subtotal defaults to total, shippingCost = 0 (logist Odesa is free).
//  - React's status "new" maps to the typed OrderStatus "processing".

const TODAY = "2026-05-29";

interface RawOrder {
  id: string;
  date: string;
  items: { p: string; q: number }[];
  status: OrderStatus;
  total: number;
}

const RAW: RawOrder[] = [
  { id: "FND-10482", date: "2026-04-16", items: [{ p: "NOIR-1K", q: 12 }, { p: "LAS-1K", q: 6 }, { p: "CUP-250", q: 4 }], status: "shipped",    total: 32940 },
  { id: "FND-10471", date: "2026-04-14", items: [{ p: "DRIP-7", q: 40 }, { p: "CAPS-10", q: 20 }],                       status: "delivered",  total: 12900 },
  { id: "FND-10465", date: "2026-04-11", items: [{ p: "ESP-BLND-1K", q: 8 }, { p: "ARB-250", q: 12 }, { p: "FLT-V60", q: 3 }], status: "delivered", total: 17228 },
  { id: "FND-10450", date: "2026-04-07", items: [{ p: "NOIR-1K", q: 10 }, { p: "LAS-250", q: 20 }],                      status: "delivered",  total: 24150 },
  { id: "FND-10441", date: "2026-04-03", items: [{ p: "GR-MAHL", q: 1 }],                                                status: "delivered",  total: 48900 },
  { id: "FND-10429", date: "2026-03-28", items: [{ p: "NOIR-1K", q: 12 }, { p: "LAS-1K", q: 6 }],                        status: "delivered",  total: 28770 },
  { id: "FND-10421", date: "2026-03-24", items: [{ p: "DRIP-7", q: 30 }],                                                status: "delivered",  total: 7050  },
  { id: "FND-10488", date: "2026-04-17", items: [{ p: "NOIR-1K", q: 8 }, { p: "ESP-BLND-1K", q: 4 }],                    status: "processing", total: 17720 },
  // React source flagged this one as "new" — collapses into "processing" under the typed shape.
  { id: "FND-10490", date: "2026-04-18", items: [{ p: "LAS-250", q: 24 }, { p: "DRIP-7", q: 10 }],                       status: "processing", total: 12790 },
];

const DAY_MS = 86_400_000;
const isOlderThan14Days = (iso: string): boolean =>
  (new Date(TODAY).getTime() - new Date(iso).getTime()) / DAY_MS > 14;

const defaultShipping: ShippingMeta = { method: "logist", address: "sklad" };

const derivePaymentStatus = (status: OrderStatus, date: string): PaymentStatus =>
  status === "delivered" && isOlderThan14Days(date) ? "paid" : "unpaid";

export const ORDERS: Order[] = RAW.map((o) => ({
  id: o.id,
  date: o.date,
  status: o.status,
  paymentStatus: derivePaymentStatus(o.status, o.date),
  shipping: defaultShipping,
  items: o.items,
  subtotal: o.total,
  shippingCost: 0,
  total: o.total,
  timeline: [{ stage: o.status, at: o.date }],
}));
